import React, { Fragment, useContext, useState, useEffect, useRef } from "react";
import Menu from "./components/Menu";
import SingleImage from "./components/SingleImage";
import Damage from "./components/Damage";
import Video from "./components/Video";
import MultipleImage from "./components/MultipleImage";
import Loading from "./components/Loading";
import Instructions from "./components/Instructions";
import Email from "./components/Email";
import Restart from "./components/Restart";
import Navbar from "./components/Navbar";
import { AppContext } from "./context";
import { useAppData } from "./context/hooks";
import { applyBtnTextColor } from "./utils_new/theme";
import { stopSessionTimeoutTimer, patLogs } from "./context/utils";
import Success from "./components/Success";
import QR from "./components/QR";
import Error from "./components/Error";
import Zeroapp from "./components/Zeroapp";
import WindshieldDamge from "./components/WindshieldDamge";
import Form from "./components/Form";
import Ping from "./components/Ping";
import NewVideo from "./components/NewVideo";
import MultipleImageSub from "./components/wondlePortraitApp/MultipleImageSub.js";
import NewZIZO from "./components/NewZIZO.js";
import * as Sentry from "@sentry/react";
// import ReactGA from "react-ga4";
import Rejection from "./components/Rejection";
import SelfieModule from "./components/SelfieModule";
import VideoRecorder from "./components/VideoRecorder";
import FormModule from "./components/FormModule";
import CustomHiibModule from "./components/CustomHiibModule";
import TimeOut from "./components/TimeOut";
import ImageUpload from "./components/ImageUpload";
import WondleMultipleImage from "./components/wondlePortraitApp/WondleMultipleImage.js";
import WondleSingleImage from "./components/wondlePortraitApp/WondleSingleImage.js";
import FormModuleWondle from "./components/wondlePortraitApp/FormModuleWondle.js";
import SuccessPort from "./components/wondlePortraitApp/SuccessPort.js";
import Email_holiday from "./components/Email_holiday.js";
import BikeStencils from "./components/BikeStencil.js";
import StencilVideo from "./components/StencilVideo.js";
import NewImgApp from "./components/NewImgApp.js";
import MenuOpes from "./components/opesApp/MenuOpes.js";
import Image360Feedback from "./components/opesApp/Image360Feedback.js";
import KarlQuestion from "./components/KarlCustom.js";
import New360ImgFeedback from "./components/New360ImgFeedback.js";
import Liberty from "./components/Liberty.js";
import LibertyReview from "./components/LibertyReview.js";
import IntegratedVideo from "./components/IntegratedVideo.js";
import NewVinOdoVideo from "./components/NewVinOdoVideo.js";
import KotakFormPage from "./components/kotakFormPage.js";
import LibertyDocument from "./components/LibertyDocument.js";
import NewCustomDocumentUpload from "./components/NewDocumentUpload.js";
import DigileraDocumentUpload from "./components/DigileraDocumentUpload.js";
import CarlFrontQuestion from "./components/CarlFrontQuestion.js";
import DamageQuestionnaire from "./components/DamageQuestionnaire.js";

/**
 * True where something other than this page owns the back gesture: any iframe,
 * a React Native WebView, or an Android WebView. Both tests are the ones the
 * app already uses elsewhere -- window.self !== window.top as in the image
 * modules, and the WebView check from the loading screen.
 *
 * The back-button guard stands down in all of them. A host has its own idea of
 * what back means, and a page-level guard only fights it.
 *
 * Known gap: an iOS WKWebView with no React Native bridge looks exactly like
 * Safari to both tests, so the guard still runs there.
 */
const backGestureOwnedByHost = () => {
  try {
    if (window.self !== window.top) return true;
  } catch (e) {
    // Reading window.top threw, which only a cross-origin parent does: framed.
    return true;
  }
  try {
    if (window.ReactNativeWebView) return true;
    return navigator.userAgent.indexOf("wv") > -1;
  } catch (e) {
    return false;
  }
};

function App() {
  const appData = useAppData();
  const { config } = useContext(AppContext);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [confirmExit, setConfirmExit] = useState(false);

  /**
   * The phone's back button, guarded across the whole app.
   *
   * This is a single page: there is no earlier screen of the inspection to go
   * back to, so a back press leaves altogether and takes every image and answer
   * captured so far with it. A sentinel entry is pushed on top of the history,
   * and each attempt to pop it is pushed straight back -- which cancels the
   * navigation -- and answered with a confirmation instead.
   *
   * The ref, not state: the listener is registered once and would otherwise
   * close over the first value forever.
   */
  const leavingRef = useRef(false);

  /**
   * True once the upload has finished on the success screen. There is nothing
   * left to lose at that point, so the back button is let alone.
   *
   * A ref for the same reason as the one above: the listener is registered once
   * and cannot read state that changes under it.
   */
  const uploadFinishedRef = useRef(false);

  useEffect(() => {
    uploadFinishedRef.current =
      (appData.screen === "success" || appData.screen === "port-success") &&
      !!appData.uploaded;
  }, [appData.screen, appData.uploaded]);

  useEffect(() => {
    // Inside a WebView or a frame the host handles back; nothing is pushed and
    // nothing is listened for, so the guard cannot fire at all.
    if (backGestureOwnedByHost()) return;

    const pushGuard = () => {
      try {
        window.history.pushState({ inspectionGuard: true }, "");
      } catch (e) {
        /* nothing to guard with; back simply behaves as it always did */
      }
    };

    const handlePop = () => {
      // The user has chosen to leave: let this one through.
      if (leavingRef.current) return;
      // Everything is uploaded and safe, so there is no warning to give. The
      // entry this guard added is spent by the press that got here, so one more
      // step back carries on out of the app and the press does what it looks
      // like it should. leavingRef stops that second pop coming back here, so
      // this cannot turn into a loop.
      if (uploadFinishedRef.current) {
        // No id passed: this runs from the listener registered on mount, which
        // closed over the first render's inspectionId (null). patLogs falls back
        // to the id it already holds.
        patLogs("back pressed after upload - leaving");
        leavingRef.current = true;
        try {
          window.history.back();
        } catch (e) {
          /* nothing behind this page: the user simply stays */
        }
        return;
      }
      pushGuard();
      patLogs("back popup shown - data loss warning");
      setConfirmExit(true);
    };

    pushGuard();
    window.addEventListener("popstate", handlePop);
    return () => window.removeEventListener("popstate", handlePop);
  }, []);

  /**
   * The client's translation for `value`, or `value` itself -- the same lookup
   * every other screen does, so the popup's built-in strings are translated
   * from config.json like the rest of the app rather than staying English.
   *
   * appData.config, not the config from useContext above: App is the provider,
   * so its own useContext sees the empty default rather than the data it is
   * about to provide.
   */
  const getTranslateText = (value) => {
    const language = appData.config && appData.config["language"];
    if (language && Object.keys(language).includes(value)) {
      return language[value];
    }
    return value;
  };

  const stayInInspection = () => {
    patLogs("back popup - user continued inspection", appData.inspectionId);
    setConfirmExit(false);
  };

  const leaveInspection = () => {
    patLogs("back popup - user chose to leave", appData.inspectionId);
    leavingRef.current = true;
    setConfirmExit(false);
    // Past the sentinel and the entry under it, which is the way out of here.
    // There is no host to notify: the guard only ever runs in a plain tab, so
    // there is no parent frame and no WebView bridge to reach. Where the link
    // was opened directly there is nothing behind it either, and a page cannot
    // close itself unless it opened itself -- so the user simply stays.
    try {
      window.history.go(-2);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    const handleSessionExpired = () => {
      // console.log("handle event listner");
      setSessionExpired(true);
    };
    window.addEventListener("session_expired", handleSessionExpired);
    return () => {
      // console.log("assign event listner");
      window.removeEventListener("session_expired", handleSessionExpired);
    };
  }, []);

  // One place publishes the client's button text colour; every button in the app
  // reads it back as var(--btn-text, #fff), so nothing needs the config passed to
  // it. appData.config, not the config from useContext above -- App is the
  // provider, so its own useContext sees the empty default rather than the data
  // it is about to provide.
  useEffect(() => {
    applyBtnTextColor(appData.config);
  }, [appData.config]);

  useEffect(() => {
    if (appData.screen === "success" || appData.screen === "port-success") {
      // console.log("suiccess reched timer end");
      stopSessionTimeoutTimer();
    }
  }, [appData.screen]);

  return (
    <AppContext.Provider value={appData}>
      <Fragment>
        <>
          {appData.screen === "loading" ? (
            <Loading />
          ) : appData.screen === "error" ? (
            <Error />
          ) : appData.screen === "rejection" ? (
            <div>
              <Navbar />
              <Rejection />
            </div>
          ) : appData.screen === "form" ? (
            <div>
              <Navbar />
              <Form />
            </div>
          ) : appData.screen === "email" ? (
            <Email />
          ) : appData.screen === "KarlQuestion" ? (
            <KarlQuestion />
          ) : appData.screen === "carlFrontQuestion" ? (
            <CarlFrontQuestion />
          ) : appData.screen === "Liberty" ? (
            <Liberty />
          ) : appData.screen === "libertyReview" ? (
            <LibertyReview />
          ) : appData.screen === "VehicleInfo" ? (
            <KotakFormPage />
          ) : appData.screen === "email_holiday" ? (
            <Email_holiday />
          ) : appData.screen === "restart" ? (
            <Restart />
          ) : appData.screen === "instructions" ? (
            <div>
              <Instructions />
            </div>
          ) : appData.screen === "menuOpes" ? (
            <MenuOpes />
          ) : appData.screen === "menu" ? (
            <Menu />
          ) : appData.screen === "block" ? (
            <SelectPage />
          ) : appData.screen === "success" ? (
            <Success />
          ) : appData.screen === "port-success" ? (
            <SuccessPort />
          ) : appData.screen === "timeOut" ? (
            <TimeOut />
          ) : (
            <SelectPage />
          )}
          {appData.screen !== "loading" && <Ping />}

          {/* Answer to a back press. appData.config, not the config from
              useContext above -- App is the provider, so its own useContext
              sees the empty default rather than the data it is about to
              provide, and every string here would fall back. */}
          {/* Answer to a back press. appData.config, not the config from
              useContext above -- App is the provider, so its own useContext
              sees the empty default rather than the data it is about to
              provide, and every string here would fall back. */}
          {confirmExit && (
            <div className="exit-confirm">
              <div className="exit-confirm-card">
                <div className="exit-confirm-icon">⚠️</div>
                <h2 className="exit-confirm-title">
                  {appData.config["backWarningHeaderText"]
                    ? appData.config["backWarningHeaderText"]
                    : getTranslateText("Going back will lose your data")}
                </h2>
                <p className="exit-confirm-text">
                  {appData.config["backWarningText"]
                    ? appData.config["backWarningText"]
                    : getTranslateText(
                      "Every image and answer captured so far will be lost and cannot be recovered. Do you want to continue the inspection?"
                    )}
                </p>
                <div className="exit-confirm-actions">
                  <button
                    className="exit-confirm-btn exit-confirm-stay"
                    onClick={stayInInspection}
                    /* The one colour that has to stay inline: it comes from the
                       client config, not the stylesheet. */
                    style={{
                      backgroundColor:
                        appData.config["colors"] &&
                        appData.config["colors"]["btn"]
                          ? appData.config["colors"]["btn"]
                          : "#006cc9",
                    }}
                  >
                    {appData.config["backWarningYesText"]
                      ? appData.config["backWarningYesText"]
                      : getTranslateText("Yes")}
                  </button>
                  <button
                    className="exit-confirm-btn exit-confirm-leave"
                    onClick={leaveInspection}
                  >
                    {appData.config["backWarningNoText"]
                      ? appData.config["backWarningNoText"]
                      : getTranslateText("No")}
                  </button>
                </div>
              </div>
            </div>
          )}

          {sessionExpired && (
            <div
              style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                backgroundColor: "rgba(10, 10, 10, 0.85)",
                backdropFilter: "blur(8px)",
                WebkitBackdropFilter: "blur(8px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 999999,
              }}
            >
              <div
                style={{
                  // background: "linear-gradient(135deg, #1e1e1e 0%, #121212 100%)",
                  backgroundColor: "white",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "16px",
                  padding: "40px 30px",
                  maxWidth: "450px",
                  width: "90%",
                  textAlign: "center",
                  boxShadow: "0 20px 40px rgba(0, 0, 0, 0.5)",
                  // color: "#ffffff",
                  color: "black",
                  fontFamily: "system-ui, -apple-system, sans-serif",
                }}
              >
                <div
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    backgroundColor: "rgba(220, 53, 69, 0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 20px auto",
                    border: "1px solid rgba(220, 53, 69, 0.3)",
                  }}
                >
                  <span style={{ fontSize: "30px", color: "#dc3545" }}>⚠️</span>
                </div>
                <h2
                  style={{
                    fontSize: "24px",
                    fontWeight: "600",
                    marginBottom: "15px",
                    letterSpacing: "-0.5px",
                  }}
                >
                  {Object.keys(config).includes("sessionExpireHeaderText")
                    ? config["sessionExpireHeaderText"]
                    : "Session Expired"}
                </h2>
                {/* <p style={{ fontSize: "15px", color: "#a0a0a0", marginBottom: "30px", lineHeight: "1.6" }}> */}
                <p
                  style={{
                    fontSize: "15px",
                    color: "black",
                    marginBottom: "30px",
                    lineHeight: "1.6",
                  }}
                >
                  {Object.keys(config).includes("sessionExpireText")
                    ? config["sessionExpireText"]
                    : "It has been more than 10 minutes since your last image upload. Your session has expired. Please contact your administrator to obtain a new inspection link."}
                </p>
                {/* <button
                  onClick={() => window.location.reload()}
                  style={{
                    width: "100%",
                    padding: "14px",
                    fontSize: "16px",
                    fontWeight: "600",
                    backgroundColor:
                      config["colors"] && config["colors"]["btn"]
                        ? config["colors"]["btn"]
                        : "#006cc9",
                    color: "white",
                    // backgroundColor: "#dc3545",
                    // color: "#ffffff",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    boxShadow: `0 4px 12px ${config["colors"] && config["colors"]["btn"]
                      ? config["colors"]["btn"]
                      : "#006cc9"}`,
                  }}
                >
                  Restart Application
                </button> */}
              </div>
            </div>
          )}
        </>
      </Fragment>
    </AppContext.Provider>
  );
}

const SelectPage = () => {
  const { currentBlock, clientId } = useContext(AppContext);
  switch (currentBlock.name) {
    case "DigileraDocumentUpload":
      return <DigileraDocumentUpload />;
    case "Liberty Document":
      return <LibertyDocument />;
    case "Custom Document Upload":
      return <NewCustomDocumentUpload />;
    case "opes 360 Image":
      return <Image360Feedback />;
    case "Stencil Video":
      return <StencilVideo />;
    case "New ZIZO Capture":
      return <NewZIZO />;
    case "damage":
      return <Damage />;
    case "Damage Questionnaire":
      return <DamageQuestionnaire />;
    case "360 Wondle Image":
      return <WondleMultipleImage />;
    case "Video Capture":
      return <Video />;
    case "QR":
      return <QR />;
    case "New Video Capture":
      return <NewVideo />;
    case "VinOdo Video Capture":
      return <IntegratedVideo />;
    case "NewVinOdo Video Capture":
      return <NewVinOdoVideo />;
    // [
    //   "client_1298_prod",
    //   "client_1298_dev",
    //   "client_1298",
    //   "client_1511",
    //   "porto_seguros_uat",
    //   "porto_seguros_claim",
    //   "porto_seguros",
    //   "client_1282",
    //   "client_1381_demo",
    //   "client_1381",
    //   "client_1381_prod",
    // ].includes(clientId) ? (
    //   <NewVideo />
    // ) : (
    //   <StencilVideo />
    // );
    case "Bike Stencil":
      return <BikeStencils />;
    case "New Image App":
      return <NewImgApp />;
    case "zeroapp":
      return <Zeroapp />;
    case "Windshield Damage":
      return <WindshieldDamge />;
    case "Selfie":
      return <SelfieModule />;
    case "Video Recorder":
      return <VideoRecorder />;
    case "Form Module":
      return <FormModule />;
    case "Form Module Portrait":
      return <FormModuleWondle />;
    case "Custom Hiib Module":
      return <CustomHiibModule />;
    case "ImageUpload":
      return <ImageUpload />;
    default:
      switch (currentBlock.subSections) {
        case true:
          switch (currentBlock.subSectionsSub) {
            case true:
              return <MultipleImageSub />;
            default:
              switch (currentBlock.fallBackBlock) {
                case true:
                  return <New360ImgFeedback />;
                default:
                  return <MultipleImage />;
              }
          }
        case false:
          if (currentBlock.subSectionsSub) {
            return <WondleSingleImage />;
          }
          return <SingleImage />;
        default:
          return <></>;
      }
  }
};

export default Sentry.withProfiler(App);
